import { useCallback } from 'react'
import { useCreateApiNotification, useFetch } from 'civic-champs-shared/api/hooks'
import { Action } from 'civic-champs-shared/api/hooks/useRemoteCollection'
import { LocationType, OrganizationLocationReporting } from 'locations/types'
import useUpdateNonSchedulableOpportunity from 'volunteering/opportunities/hooks/useUpdateNonSchedulableOpportunity'
import { OpportunityPayload } from 'Event/interfaces'
import useCancelOpportunities from 'Event/hooks/useCancelOpportunities'
import { CancelType } from 'Event/modules/events/details/header'

export type UseEndOpportunityFromLocationsOptions = {
  replace?: Action<OrganizationLocationReporting>
  showNotification?: boolean
  eager?: boolean
}

export const useEndOpportunityFromLocations = ({
  replace,
  showNotification = true,
  eager = true,
}: UseEndOpportunityFromLocationsOptions = {}) => {
  const createNotification = useCreateApiNotification()
  const [updateOpportunity] = useUpdateNonSchedulableOpportunity(false) as [
    (opportunity: Partial<OpportunityPayload>, encodedOccurrence: string) => Promise<any>,
  ]
  const cancelOpportunities = useCancelOpportunities({
    useNewNotification: false,
  })
  const [requestEvent] = useFetch() as [
    (requestConfig: { url: string }) => Promise<{ totalCheckIns?: number }>,
    unknown,
  ]

  return useCallback(
    async (location: OrganizationLocationReporting) => {
      if (!location.encodedOccurrence) {
        return
      }

      let undo
      const clearedLocation = {
        ...location,
        timesUsedActive: 0,
        timesUsedTotal: 0,
        encodedOccurrence: null,
        type: LocationType.Event,
      }
      if (eager) {
        undo = replace?.(clearedLocation)
      }
      const notification = showNotification ? createNotification('Updating location') : undefined
      try {
        const event = await requestEvent({
          url: `/opportunity_instances/${location.encodedOccurrence}/?populateRecurrenceInfo=false`,
        })
        const hasCheckIns = (event?.totalCheckIns ?? 0) > 0

        if (hasCheckIns) {
          await updateOpportunity(
            {
              geofencing: location.geofencing,
              startsAt: location.startsAt as string,
              name: location.name as string,
              endsAt: new Date(),
            },
            location.encodedOccurrence,
          )
          if (!eager) {
            replace?.({ ...clearedLocation, timesUsedTotal: location.timesUsedTotal })
          }
        } else {
          await cancelOpportunities([location.encodedOccurrence], CancelType.All)
          if (!eager) {
            replace?.(clearedLocation)
          }
        }

        notification?.onSuccess('Location updated')
      } catch (error) {
        notification?.onError(`Could not update ${location.name || 'selected'} location`, error)
        undo?.()
        throw error
      }
    },
    [cancelOpportunities, createNotification, eager, replace, requestEvent, showNotification, updateOpportunity],
  )
}

export default useEndOpportunityFromLocations
